import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Chrome";
import { useSettings } from "../lib/settings";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

export default function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const currency = settings?.currency || "$";

  const load = () => {
    setLoading(true);
    api.get("/orders/mine")
      .then(r => setOrders(r.data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cancel = async (o) => {
    if (!window.confirm(`Cancel order #${o.inquiry_no}? Reserved stock will be returned to inventory.`)) return;
    try {
      await api.post(`/orders/${o.id}/cancel`);
      toast.success(`Order #${o.inquiry_no} cancelled`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const badge = (s) => ({
    pending: "bg-safety text-ink",
    confirmed: "bg-cobalt text-paper",
    shipped: "bg-ink text-paper",
    delivered: "bg-green-700 text-paper",
    cancelled: "bg-soda text-paper",
  }[s] || "bg-black/10");

  const payLabel = (m) => ({
    cash: "Cash",
    electronic_or_cheque: "E-Transfer / Cheque",
    etransfer: "E-Transfer / Cheque",
    cheque: "E-Transfer / Cheque",
  }[m] || m);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <div className="pt-28 md:pt-32 px-6 md:px-12 lg:px-16 pb-16" data-testid="portal-orders-page">
        <div className="overline text-black/50 mb-2">Retailer portal</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">My orders.</h1>
        <div className="mt-4"><Link to="/portal" className="text-xs underline">← Back to portal</Link></div>

        {loading ? (
          <div className="mt-8 text-sm text-black/60">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="mt-8 border border-ink p-8 md:p-12 text-center max-w-2xl" data-testid="portal-orders-empty">
            <p className="text-black/60 mb-4">You haven't placed any orders yet.</p>
            <Link to="/catalog" className="btn-primary">Browse catalog</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="portal-orders-list">
            {orders.map(o => (
              <div key={o.id} className="border border-ink bg-paper p-4 md:p-5" data-testid={`portal-order-${o.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <div className="font-display text-2xl md:text-3xl uppercase leading-none">#{o.inquiry_no}</div>
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${badge(o.status)}`}>{o.status}</span>
                    </div>
                    <div className="text-xs text-black/50">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="text-xs text-black/70 mt-1 capitalize">{o.delivery_method} · {payLabel(o.payment_method || "cash")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl md:text-3xl">{currency}{o.subtotal?.toFixed(2)}</div>
                    <div className="text-xs text-black/50">{o.lines?.length || 0} lines · {o.lines?.reduce((n, l) => n + l.quantity, 0)} units</div>
                    {(o.cash_subtotal > 0 || o.electronic_subtotal > 0) && (
                      <div className="mt-1 text-[11px] text-black/70 flex flex-col items-end gap-0.5" data-testid={`portal-order-split-${o.id}`}>
                        {o.cash_subtotal > 0 && <span>Cash: {currency}{o.cash_subtotal.toFixed(2)}</span>}
                        {o.electronic_subtotal > 0 && <span>E-Transfer/Cheque: {currency}{o.electronic_subtotal.toFixed(2)}</span>}
                      </div>
                    )}
                    <div className="flex gap-3 items-center justify-end mt-2">
                      <Link to={`/order/${o.id}/print`} target="_blank" className="text-[11px] underline" data-testid={`portal-order-print-${o.id}`}>Print order form</Link>
                      {o.status === "pending" && (
                        <button onClick={() => cancel(o)} className="text-[11px] underline text-soda" data-testid={`portal-order-cancel-${o.id}`}>Cancel</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
